import { KompisOpenDesign } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
  OpenDesignStateEnum,
  OpenDesignModalEnum,
  OpenDesignToastMessage,
  OpenDesignTabEnum
} from '@inter-ikea-kompis/component-open-design';
import { OpenDesignCodeStateEnum } from '@inter-ikea-kompis/component-open-design-code';
import { ToastManager } from '@inter-ikea-kompis/component-toast';
import { useEffect, useState } from 'react';
import { VpcService } from '@inter-ikea-kompis/services';
import { useServiceSettings } from 'contexts/serviceSettings';
import {
  hideOpenDesignSheet,
  isOpenDesignSheetOpen,
  isShowSavedDesignsTabEnabled
} from 'ducks/sheets';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'ducks/hooks';
import { ISaveConfigurationsItem } from '@inter-ikea-kompis/types';
import { isKiosk, isProduction } from 'utils/platform';
import { useSceneHandler } from 'contexts/sceneHandler';
import { AppRoutes } from 'routes';
import { useHistory } from 'react-router-dom';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import { ReportSourceEnum, reportDesignErrorScreen } from 'analytics/reporters';
import { startGame } from 'rangeConfig/eket/features/Ekris/EkrisFeature';
import { getRange } from 'utils/queryUtils';
import { useSaveDesign } from 'hooks/useSaveDesign';
import { useServices } from 'contexts/services';

function OpenDesignToProfile() {
  const dispatch = useAppDispatch();
  const { kompisSettings, translations, dexfSettings } = useServiceSettings();
  const { accountService, saveService } = useServices();
  const { loadVpcScene, loadDesignIdScene } = useSceneHandler();
  const history = useHistory();
  const enableEkris = !isProduction() && getRange() === 'eket';
  const visible = useSelector(isOpenDesignSheetOpen);
  const showSavedDesignsTab = useSelector(isShowSavedDesignsTabEnabled);
  const vpcService = new VpcService(kompisSettings);

  const [visibleModal, setVisibleModal] = useState<OpenDesignModalEnum | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [savedDesigns, setSavedDesigns] = useState<ISaveConfigurationsItem[]>([]);
  const [openSavedDesignState, setOpenSavedDesignState] = useState<OpenDesignStateEnum>(
    OpenDesignStateEnum.default
  );
  const [openDesignCodeState, setOpenDesignCodeState] = useState<OpenDesignCodeStateEnum>(
    OpenDesignCodeStateEnum.default
  );
  const { initiateLogin } = useSaveDesign();

  useEffect(() => {
    if (visible) {
      setVisibleModal(OpenDesignModalEnum.openDesignCard);
    } else {
      setVisibleModal(null);
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      accountService()
        .getUser()
        .then(user => {
          setLoggedIn(!!user);

          if (user) {
            return saveService
              .getConfigurations({
                token: user.accessToken,
                filters: { applications: [kompisSettings.dexfApplicationId] }
              })
              .then(setSavedDesigns);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const resetState = (): void => {
    setOpenDesignCodeState(OpenDesignCodeStateEnum.default);
    setOpenSavedDesignState(OpenDesignStateEnum.default);
  };

  const _initiateLogin = () => {
    initiateLogin(ReportSourceEnum.openDesignSheet, { state: 'openDesign' });
  };

  const showToast = (success: boolean) =>
    new ToastManager({ theme: SkapaTheme }).showToast(
      success
        ? OpenDesignToastMessage.getOpenDesignSuccess(translations.kompis)
        : OpenDesignToastMessage.getOpenDesignFailure(translations.kompis)
    );

  const rejectIfNoValue = <T,>(value: T) => value ?? Promise.reject();

  const onOpenDesignCode = ({ detail: { designCode } }: CustomEvent) => {
    if (!designCode) return;

    if (enableEkris && designCode.toLowerCase() === 'ekris') {
      history.push(AppRoutes.PLANNER);
      dispatch(startGame());
      closeSheetAndResetState();
      return;
    }

    setOpenDesignCodeState(OpenDesignCodeStateEnum.loading);

    vpcService
      .getConfiguration(designCode)
      .then(rejectIfNoValue)
      .then(() => handleLoadVpc(designCode))
      .then(() => {
        closeSheetAndResetState();
        showToast(true);
      })
      .catch(() => {
        setOpenDesignCodeState(OpenDesignCodeStateEnum.failed);
      });
  };

  const handleLoadVpc = (vpcCode: string) =>
    loadVpcScene(vpcCode, DesignSourceEnum.enterCode)
      .then(scene => scene?.init())
      .then(rejectIfNoValue)
      .then(scene => {
        if (scene.initialized) {
          history.push(AppRoutes.PLANNER);
        } else if (scene.legacy) {
          history.push(AppRoutes.SUMMARY);
        } else {
          return Promise.reject();
        }
      });

  const onOpenSavedDesign = async ({ detail: { designId } }: CustomEvent) => {
    if (!designId) return;

    setOpenSavedDesignState(OpenDesignStateEnum.loading);

    accountService()
      .getUser()
      .then(async user => {
        if (user) {
          const scene = await loadDesignIdScene(
            designId,
            DesignSourceEnum.profile,
            saveService,
            user.accessToken
          );

          if (scene) {
            scene.init();
            showToast(true);
            history.push(AppRoutes.PLANNER);
          }
        } else {
          // This likely means the token has expired. Prompt the user to log in again.
          _initiateLogin();
        }
      })
      .catch(error => {
        reportDesignErrorScreen({ designId });
        showToast(false);
        throw error;
      })
      .finally(() => {
        closeSheetAndResetState();
      });
  };

  const onModalClose = (): void => {
    dispatch(hideOpenDesignSheet());
    // Reset state on modal close.
    resetState();
  };

  const closeSheetAndResetState = () => {
    dispatch(hideOpenDesignSheet());
    resetState();
  };

  return (
    translations &&
    dexfSettings && (
      <KompisOpenDesign
        theme={SkapaTheme}
        translations={translations.kompis}
        settings={dexfSettings}
        visibleModal={visibleModal}
        loggedIn={loggedIn}
        enableTabs={!isKiosk() && showSavedDesignsTab}
        selectedTab={
          showSavedDesignsTab ? OpenDesignTabEnum.ikeaProfile : OpenDesignTabEnum.designCode
        }
        savedDesigns={savedDesigns}
        openSavedDesignState={openSavedDesignState}
        openDesignCodeState={openDesignCodeState}
        onModalClose={onModalClose}
        onLogIn={_initiateLogin}
        onOpenDesignCode={onOpenDesignCode}
        onOpenSavedDesign={onOpenSavedDesign}
        onResetOpenDesignCodeState={({ detail }) =>
          setOpenDesignCodeState(detail.openDesignCodeState)
        }
      />
    )
  );
}

export default OpenDesignToProfile;
